import React, { useState, useEffect } from 'react';
import { Shield, X, ChevronDown, ChevronUp } from 'lucide-react';
import { CONSENT_KEY, initPixel, clearMetaCookies } from '../utils/cookieConsent';

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
    backdropFilter: 'blur(4px)', zIndex: 9998, display: 'flex',
    alignItems: 'flex-end', justifyContent: 'center', padding: '0 0 24px',
  },
  banner: {
    background: '#0D0D12', border: '1.5px solid rgba(255,255,255,.1)',
    borderRadius: 20, padding: '20px 24px 20px',
    maxWidth: 520, width: '90%', boxShadow: '0 24px 60px rgba(0,0,0,.5)',
    fontFamily: "'Söhne','Inter',-apple-system,sans-serif",
  },
  title: { fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 6 },
  desc: { fontSize: 12.5, color: 'rgba(255,255,255,.5)', lineHeight: 1.6, marginBottom: 14 },
  catRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)',
    borderRadius: 10, padding: '10px 14px', marginBottom: 8, cursor: 'default',
  },
  catLabel: { fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.85)' },
  catDesc: { fontSize: 11.5, color: 'rgba(255,255,255,.4)', lineHeight: 1.55, marginTop: 6 },
  toggle: {
    width: 40, height: 22, borderRadius: 11, border: 'none',
    cursor: 'pointer', position: 'relative', flexShrink: 0, transition: 'background .2s',
  },
  toggleKnob: {
    position: 'absolute', top: 3, width: 16, height: 16, borderRadius: '50%',
    background: '#fff', transition: 'left .2s',
  },
  btnRow: { display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' },
  btnPrimary: {
    flex: 1, padding: '10px 18px', borderRadius: 10, background: '#1B5AFF',
    color: '#fff', fontWeight: 700, fontSize: 13.5, border: 'none',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  btnSecondary: {
    flex: 1, padding: '10px 18px', borderRadius: 10,
    background: 'rgba(255,255,255,.07)', color: 'rgba(255,255,255,.75)',
    fontWeight: 600, fontSize: 13.5, border: '1px solid rgba(255,255,255,.1)',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  link: { color: 'rgba(255,255,255,.3)', fontSize: 11.5, textDecoration: 'none' },
};

function Toggle({ on, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!on)}
      style={{ ...S.toggle, background: on ? '#1B5AFF' : 'rgba(255,255,255,.15)', cursor: disabled ? 'default' : 'pointer' }}
      aria-label={on ? 'Enabled' : 'Disabled'}
    >
      <div style={{ ...S.toggleKnob, left: on ? 21 : 3 }} />
    </button>
  );
}

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) { setShow(true); }

    // Allow any component to reopen settings via custom event
    const reopen = () => { setShow(true); setMarketing(localStorage.getItem(CONSENT_KEY) === 'all'); };
    window.addEventListener('vw:openCookieSettings', reopen);
    return () => window.removeEventListener('vw:openCookieSettings', reopen);
  }, []);

  const save = (choice) => {
    localStorage.setItem(CONSENT_KEY, choice);
    // Also write to vett_cookie_consent for gtag compatibility
    const prefs = {
      essential: true,
      analytics: choice === 'all',
      marketing: choice === 'all',
    };
    localStorage.setItem('vett_cookie_consent', JSON.stringify(prefs));
    // Update Google Consent Mode
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        ad_user_data: prefs.marketing ? 'granted' : 'denied',
        ad_personalization: prefs.marketing ? 'granted' : 'denied',
      });
    }
    if (choice === 'all') {
      initPixel(); // Initialize Pixel NOW that consent is given
    } else {
      clearMetaCookies(); // Expire _fbp and _fbc immediately
    }
    setShow(false);
  };

  const handleAcceptAll = () => save('all');
  const handleEssentialOnly = () => save('essential');
  const handleSavePrefs = () => {
    // Save with exact marketing preference
    const choice = marketing ? 'all' : 'essential';
    save(choice);
  };

  if (!show) return null;

  return (
    <div style={S.overlay} data-testid="cookie-banner">
      <div style={S.banner}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Shield size={16} color="#1B5AFF" />
          <span style={S.title}>Cookie preferences</span>
        </div>
        <p style={S.desc}>
          We use cookies to operate this platform and, with your consent, to measure advertising performance via Meta Pixel.
          {' '}<a href="/privacy.html" style={S.link}>Privacy Policy</a>
        </p>

        {/* Categories */}
        <div>
          {/* Essential — always on */}
          <div style={S.catRow}>
            <div style={{ flex: 1 }}>
              <div style={S.catLabel}>Essential cookies</div>
              {expanded && (
                <div style={S.catDesc}>
                  Authentication, session management, and security tokens. Required for the platform to function. Cannot be disabled.
                </div>
              )}
            </div>
            <Toggle on disabled />
          </div>

          {/* Marketing — off by default */}
          <div style={{ ...S.catRow, cursor: 'pointer' }} onClick={() => setMarketing(m => !m)}>
            <div style={{ flex: 1 }}>
              <div style={S.catLabel}>Marketing &amp; advertising</div>
              {expanded && (
                <div style={S.catDesc}>
                  Meta Pixel (<code style={{ fontSize: 11 }}>_fbp</code>, <code style={{ fontSize: 11 }}>_fbc</code>) and Meta Conversions API.
                  Measures advertising effectiveness. Your email and activity are SHA-256 hashed — never sent in plain text.
                </div>
              )}
            </div>
            <Toggle on={marketing} onChange={setMarketing} />
          </div>
        </div>

        {/* Show/hide details link */}
        <button
          onClick={() => setExpanded(e => !e)}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.35)', fontSize: 11.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0', fontFamily: 'inherit' }}
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? 'Hide details' : 'Show details'}
        </button>

        {/* Action buttons */}
        <div style={S.btnRow}>
          <button style={S.btnPrimary} onClick={handleAcceptAll} data-testid="accept-all-btn">
            Accept all
          </button>
          <button style={S.btnSecondary} onClick={handleEssentialOnly} data-testid="essential-only-btn">
            Essential only
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <button
            style={{ ...S.btnSecondary, width: '100%', background: 'transparent', border: 'none', fontSize: 12, color: 'rgba(255,255,255,.3)' }}
            onClick={handleSavePrefs}
            data-testid="save-prefs-btn"
          >
            Save my preferences
          </button>
        </div>
      </div>
    </div>
  );
}
